import { z } from 'zod';

import { StepName } from '@website/hooks/useBookingSteps';

import { BookingFormData, BookingFormSchema, TimeSlotSchema } from './bookingSchema';

/**
 * Single source of truth for "does this booking need a client address?".
 *
 * An address is required only for an in-person AT-HOME session, gated on the
 * CHOSEN slot's own resolved 3-value location kind (a mixed activity may offer
 * venue and at_home slots side by side). A venue or online pick is location-less
 * for the client address. Falls back to the activity-level at-home hint only when
 * the per-slot location was not captured (legacy slots with no referenced
 * location), and even then an explicit online location skips the address.
 *
 * The stepper (useBookingSteps), the continue router (useStepButton), and these
 * validators all key off THIS helper so they can never diverge again.
 */
export const needsAddressForSlot = (
  slotKind: 'venue' | 'at_home' | 'online' | undefined,
  fallbackIsAtHome: boolean,
  fallbackLocationKind?: 'venue' | 'at_home' | 'online'
): boolean => {
  if (slotKind) {
    return slotKind === 'at_home';
  }
  return fallbackIsAtHome && fallbackLocationKind !== 'online';
};

const needsAddress = (formData: Partial<BookingFormData>, isAtHomeActivity: boolean): boolean =>
  needsAddressForSlot(formData.slotLocation?.kind, isAtHomeActivity, formData.locationKind);

export const validateBookingDate = (dateString: string): boolean => {
  const bookingDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  bookingDate.setHours(0, 0, 0, 0);

  return bookingDate >= today;
};

export const validateCurrentStep = (
  data: unknown,
  currentStep: StepName,
  isAtHomeActivity: boolean = false
): { success: boolean; errors: string[] } => {
  const errors: string[] = [];

  try {
    const formData = data as BookingFormData;

    switch (currentStep) {
      case 'select-time':
        // Validate slot type + date + time + people (if GROUP)
        if (!formData.bookingType) {
          errors.push('Slot type is required');
        }

        if (!formData.bookingDate) {
          errors.push('Booking date is required');
        } else if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.bookingDate)) {
          errors.push('Date must be in YYYY-MM-DD format');
        } else if (!validateBookingDate(formData.bookingDate)) {
          errors.push('Booking date cannot be in the past');
        }

        if (!formData.timeSlot) {
          errors.push('Time slot is required');
        } else {
          const timeSlotResult = TimeSlotSchema.safeParse(formData.timeSlot);
          if (!timeSlotResult.success) {
            errors.push(...timeSlotResult.error.errors.map((err) => err.message));
          }
        }

        if (formData.bookingType === 'GROUP' && (!formData.numberOfPeople || formData.numberOfPeople < 1)) {
          errors.push('Number of people is required for group slots');
        }
        break;

      case 'select-address': {
        // Validate all previous + address
        const timeValidation = validateCurrentStep(data, 'select-time', isAtHomeActivity);
        errors.push(...timeValidation.errors);

        // Online picks are location-less - no address even for an at-home activity.
        if (needsAddress(formData, isAtHomeActivity) && !formData.address) {
          errors.push('Address is required for at-home activities');
        }

        if (formData.addressOutOfRadius) {
          errors.push("Address is outside the professional's service area");
        }
        break;
      }

      case 'payment': {
        // Validate all previous steps based on whether an address is actually needed
        // (an online pick skips the address step even for an at-home activity).
        if (needsAddress(formData, isAtHomeActivity)) {
          const addressValidation = validateCurrentStep(data, 'select-address', isAtHomeActivity);
          errors.push(...addressValidation.errors);
        } else {
          const timeValidation = validateCurrentStep(data, 'select-time', isAtHomeActivity);
          errors.push(...timeValidation.errors);
        }
        break;
      }

      case 'status':
        // Full validation for final submission
        return validateBookingForm(data, isAtHomeActivity);

      default:
        return { success: true, errors: [] };
    }

    return { success: errors.length === 0, errors };
  } catch {
    return { success: false, errors: ['Invalid booking data'] };
  }
};

export const validateBookingForm = (
  data: unknown,
  isAtHomeActivity: boolean = false
): { success: boolean; errors: string[] } => {
  const errors: string[] = [];

  try {
    const validatedData: BookingFormData = BookingFormSchema.parse(data);

    if (!validateBookingDate(validatedData.bookingDate)) {
      errors.push('Booking date cannot be in the past');
    }

    if (validatedData.bookingType === 'GROUP' && (!validatedData.numberOfPeople || validatedData.numberOfPeople < 1)) {
      errors.push('Number of people is required for group slots');
    }

    if (needsAddress(validatedData, isAtHomeActivity) && !validatedData.address) {
      errors.push('Address is required for at-home activities');
    }

    return { success: errors.length === 0, errors };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        errors: error.errors.map((err) => err.message)
      };
    }

    return {
      success: false,
      errors: ['Invalid booking data']
    };
  }
};
